import React from 'react'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { RadioButtonStripField } from 'view/components/buttons'
import { EntityStatus, Organisation, OrganisationFeature, OrganisationType, PlatformFeature, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { ToggleField } from 'view/components/toggle'
import { OrganisationTypeMultiComboboxField } from 'view/shared/entity'
import { FeatureToggleTable } from '../../platform/PlatformSettings'
import { FeatureToggle } from 'view/shell/FeatureToggle'

export interface OrganisationDetailsPanelProps {
  organisation: Organisation
  validationIssues: ValidationIssue[]
  readOnly: boolean
  onUpdate: (organisation: Partial<Organisation>) => unknown
  marketStatusClosed: boolean
}

const statusTranslations = {
  [EntityStatus.ENABLED]: Translation.TC_ACTIVE,
  [EntityStatus.DISABLED]: Translation.TC_SUSPENDED
}

export const OrganisationDetailsPanel: React.FunctionComponent<OrganisationDetailsPanelProps> = props => {
  const { organisation, validationIssues, readOnly, onUpdate, marketStatusClosed } = props

  const updateHandler = <K extends keyof Organisation>(key: K) => {
    return (value: Organisation[K]) => {
      const update = {
        [key]: value
      }
      onUpdate(update)
    }
  }

  const organisationPlatformFeatureUpdateHandler = (feature: PlatformFeature) => {
    return (enabled: boolean) => {
      const update: Organisation = {
        ...organisation,
        enabledFeatures: enabled
          ? [...organisation.enabledFeatures, feature]
          : organisation.enabledFeatures.filter(platformFeature => !platformFeature.includes(feature))
      }
      onUpdate(update)
    }
  }

  const organisationFeatureUpdateHandler = (feature: OrganisationFeature) => {
    return (enabled: boolean) => {
      const update: Organisation = {
        ...organisation,
        enabledOrganisationFeatures: enabled
          ? [...organisation.enabledOrganisationFeatures, feature]
          : organisation.enabledOrganisationFeatures.filter(organisationFeature => !organisationFeature.includes(feature))
      }
      onUpdate(update)
    }
  }

  const statusUpdateHandler = (value: Translation) => {
    updateHandler('status')(value === Translation.TC_ACTIVE ? EntityStatus.ENABLED : EntityStatus.DISABLED)
  }

  const organisationPlatformFeatureEnabled = (feature: PlatformFeature) => {
    return organisation && organisation.enabledFeatures.includes(feature)
  }

  const organisationFeatureEnabled = (feature: OrganisationFeature) => {
    return organisation && organisation.enabledOrganisationFeatures.includes(feature)
  }

  const isBuySideOrganisation = organisation.organisationTypes.includes(OrganisationType.BUY_SIDE)

  return (
    <>
      <DialogRow verticalLayout={constants.VerticalLayout.Bottom}>
        <DialogField>
          <TextInputField
            dataTestId={'OrganisationDetailsPanel-LegalName'}
            label={Locale.translate(Translation.SC_LEGAL_NAME)}
            fieldName="legalName"
            validationIssues={validationIssues}
            value={organisation.legalName}
            maxLength={constants.nameMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('legalName')}
          />
        </DialogField>
        <DialogField flex={0}>
          <RadioButtonStripField
            dataTestId={'OrganisationDetailsPanel-RadioButtonStripField'}
            label={Locale.translate(Translation.SC_TRADING)}
            options={[Translation.TC_ACTIVE, Translation.TC_SUSPENDED]}
            selectedOption={statusTranslations[organisation.status]}
            buttonWidth={80}
            disabled={readOnly}
            onUpdate={statusUpdateHandler}
          />
        </DialogField>
      </DialogRow>
      <DialogRow>
        <DialogField>
          <TextInputField
            dataTestId={'OrganisationDetailsPanel-OrganisationLEI'}
            label={Locale.translate(Translation.SC_ORGANISATION_LEI)}
            fieldName="lei"
            validationIssues={validationIssues}
            value={organisation.lei}
            maxLength={constants.leiMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('lei')}
          />
        </DialogField>
        <DialogField>
          <TextInputField
            dataTestId={'OrganisationDetailsPanel-ShortCode'}
            label={Locale.translate(Translation.SC_SHORT_CODE)}
            fieldName="shortName"
            validationIssues={validationIssues}
            value={organisation.shortCode}
            maxLength={constants.shortIdMaxFieldLength}
            readOnly={readOnly}
            onUpdate={updateHandler('shortCode')}
          />
        </DialogField>
      </DialogRow>

      <DialogRow>
        <DialogField>
          <OrganisationTypeMultiComboboxField
            dataTestId={'OrganisationDetailsPanel-OrganisationType'}
            label={Locale.translate(Translation.SC_ORGANISATION_TYPE)}
            fieldName="organisationTypes"
            validationIssues={validationIssues}
            placeholder={Locale.translate(Translation.SC_SELECT_ORGANISATION_TYPE)}
            value={organisation.organisationTypes}
            readOnly={readOnly}
            onUpdate={updateHandler('organisationTypes')}
          />
        </DialogField>
      </DialogRow>
      <FeatureToggleTable>
        <thead>
          <tr>
            <th>{Locale.translate(Translation.TC_FEATURE_TOGGLE)}</th>
            <th>{Locale.translate(Translation.TC_ENABLED)}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{Locale.translate(Translation.SC_START_CONSIDERATION_OVERRIDE_ENABLED)}</td>
            <td>
              <FeatureToggle feature={PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE}>
                <ToggleField
                  dataTestId={'OrganisationDetailsPanel-EnableStartConsiderationOverride'}
                  label={''}
                  validationIssues={validationIssues}
                  checked={organisationPlatformFeatureEnabled(PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE)}
                  readOnly={readOnly || !marketStatusClosed}
                  onUpdate={organisationPlatformFeatureUpdateHandler(PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE)}
                />
              </FeatureToggle>
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.SC_FIX_PERCENTAGE_FORMAT_ENABLED)}</td>
            <td>
              <ToggleField
                dataTestId={'OrganisationDetailsPanel-EnableFixMessageFormatPercentage'}
                label={''}
                validationIssues={validationIssues}
                checked={organisationPlatformFeatureEnabled(PlatformFeature.ENABLE_FIX_PERCENTAGE_FORMAT)}
                readOnly={readOnly || !marketStatusClosed}
                onUpdate={organisationPlatformFeatureUpdateHandler(PlatformFeature.ENABLE_FIX_PERCENTAGE_FORMAT)}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.SC_STOCK_BORROW_LENDING)}</td>
            <td>
              <ToggleField
                dataTestId={'OrganisationDetailsPanel-EnableStockBorrowLending'}
                label={''}
                validationIssues={validationIssues}
                checked={organisationPlatformFeatureEnabled(PlatformFeature.ENABLE_STOCK_BORROWING_LENDING)}
                readOnly={readOnly || !marketStatusClosed}
                onUpdate={organisationPlatformFeatureUpdateHandler(PlatformFeature.ENABLE_STOCK_BORROWING_LENDING)}
              />
            </td>
          </tr>
          <tr>
            <td>{Locale.translate(Translation.TC_PRE_ASSIGNMENT_TOGGLE)}</td>
            <td>
              <ToggleField
                dataTestId={'OrganisationDetailsPanel-EnablePreAssignment'}
                label={''}
                validationIssues={validationIssues}
                checked={organisationFeatureEnabled(OrganisationFeature.ENABLE_PRE_ASSIGNMENT)}
                readOnly={readOnly || !marketStatusClosed || !isBuySideOrganisation}
                onUpdate={organisationFeatureUpdateHandler(OrganisationFeature.ENABLE_PRE_ASSIGNMENT)}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.TC_LOCK_FIX_TOGGLE)}</td>
            <td>
              <ToggleField
                dataTestId={'OrganisationDetailsPanel-EnableLockFix'}
                label={''}
                validationIssues={validationIssues}
                readOnly={readOnly}
                checked={organisationFeatureEnabled(OrganisationFeature.ENABLE_FIX_LOCKING)}
                onUpdate={organisationFeatureUpdateHandler(OrganisationFeature.ENABLE_FIX_LOCKING)}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.TC_GRID_VIEW_TOGGLE)}</td>
            <td>
              <ToggleField
                dataTestId={'OrganisationDetailsPanel-EnableGridView'}
                label={''}
                validationIssues={validationIssues}
                checked={organisationFeatureEnabled(OrganisationFeature.ENABLE_GRID_VIEW)}
                readOnly={readOnly || !isBuySideOrganisation}
                onUpdate={organisationFeatureUpdateHandler(OrganisationFeature.ENABLE_GRID_VIEW)}
              />
            </td>
          </tr>

          <tr>
            <td>{Locale.translate(Translation.TC_CUMULATIVE_RECONCILIATION_TOGGLE)}</td>
            <td>
              <ToggleField
                dataTestId={'OrganisationDetailsPanel-EnableCumulativeReconciliation'}
                label={''}
                validationIssues={validationIssues}
                checked={organisationFeatureEnabled(OrganisationFeature.ENABLE_CUMULATIVE_RECONCILIATION_FILE)}
                readOnly={readOnly}
                onUpdate={organisationFeatureUpdateHandler(OrganisationFeature.ENABLE_CUMULATIVE_RECONCILIATION_FILE)}
              />
            </td>
          </tr>
        </tbody>
      </FeatureToggleTable>
    </>
  )
}
